import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  createLiveLink,
  listLiveLinks,
  WEBREQUEST_ORIGIN,
} from './liveTrackDataFeeds';

// Connect-your-MT5 wizard.
//
// The whole feature hinges on one step that has nothing to do with our code: the user has to
// tick a checkbox in MetaTrader's options and paste a URL. Get that wrong and the EA fails
// silently with error 4014, which looks exactly like "the website is broken".
//
// So this screen does three things a plain instructions page would not:
//   1. Shows the exact URL as a one-click copy, rather than asking the user to type it.
//   2. Numbers the MetaTrader steps in the order they appear in the actual dialog.
//   3. POLLS for the first ping and flips to a success state the moment it arrives, so the
//      user gets an unambiguous "it worked" instead of wondering whether to keep waiting.

const STEPS = ['Get your token', 'Allow the URL in MT5', 'Turn it on', 'Confirm'];

function CopyField({ value, label }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="live-copy-field">
      {label && <span className="muted-text">{label}</span>}
      <div>
        <code>{value}</code>
        <button
          type="button"
          className="ghost-button compact-button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(value);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch (error) {
              // Clipboard can be blocked (insecure origin, permissions). The value is on
              // screen and selectable, so this is a nicety rather than a requirement.
              setCopied(false);
            }
          }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

function ConnectMt5Wizard({ strategies, onClose, onConnected }) {
  const [step, setStep] = useState(0);
  const [link, setLink] = useState(null);
  const [strategyId, setStrategyId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [waiting, setWaiting] = useState(false);
  const [connectedAccount, setConnectedAccount] = useState(null);
  const pollRef = useRef(null);

  const [reusedExisting, setReusedExisting] = useState(false);

  // Reuse the account's existing token rather than minting a new one.
  //
  // Exporting an EA already creates a link and bakes that token into the download
  // (resolveVixLinkToken). If this always created a fresh one, the token shown here would not
  // match the token inside the EA the user just downloaded — two valid links, two separate
  // tracked accounts, and a very reasonable "which one is mine?". One token per account is the
  // mental model; extra terminals are handled by the tokens table, not by this wizard.
  const createToken = async () => {
    setBusy(true);
    setError('');
    try {
      const { links } = await listLiveLinks();
      // Copier links are skipped. The signal copier keeps its own link and is handed that
      // token in its feed; showing it here would tell the user to paste the copier's token
      // into a builder EA, filing that EA's results under a strategy-less link.
      const existing = (links || []).find(
        (item) => item.status === 'active' && item.source !== 'copier',
      );
      if (existing) {
        setLink(existing);
        setReusedExisting(true);
      } else {
        const result = await createLiveLink({
          label: 'My MT5 account',
          strategyId: strategyId ? Number(strategyId) : undefined,
        });
        setLink(result.link);
        setReusedExisting(false);
      }
      setStep(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  // Poll for the first payload. This is the difference between "did it work?" and a clear yes.
  const startWaiting = useCallback(() => {
    setWaiting(true);
    setStep(3);
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { links } = await listLiveLinks();
        const mine = (links || []).find((item) => item.id === link?.id);
        const account = (mine?.accounts || [])[0];
        if (account) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setWaiting(false);
          setConnectedAccount(account);
        }
      } catch (err) { /* keep waiting; transient errors are expected */ }
    }, 4000);
  }, [link]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  return (
    <div className="signal-wizard-backdrop" role="dialog" aria-modal="true" aria-label="Connect MetaTrader 5">
      <div className="signal-wizard">
        <header className="signal-wizard-head">
          <div>
            <h3>Connect your MetaTrader 5</h3>
            <p className="muted-text">
              Your EA reports its results back here so you can track them from anywhere.
            </p>
          </div>
          <button type="button" className="ghost-button compact-button" onClick={onClose}>Close</button>
        </header>

        <ol className="signal-wizard-steps">
          {STEPS.map((label, index) => (
            <li key={label} className={`${index === step ? 'active' : ''} ${index < step ? 'done' : ''}`}>
              <span className="signal-step-dot">{index < step ? '✓' : index + 1}</span>
              {label}
            </li>
          ))}
        </ol>

        <div className="signal-wizard-body">
          {step === 0 && (
            <div className="signal-step">
              <p className="muted-text">
                This is the private token that identifies your account. It is already built into
                any EA you export from here, so most of the time you will not need to copy it —
                the wizard reuses your existing token rather than creating a second one.
              </p>
              <label className="signal-field">
                <span>Link to a strategy (optional)</span>
                <select value={strategyId} onChange={(event) => setStrategyId(event.target.value)}>
                  <option value="">Not linked</option>
                  {(strategies || []).map((strategy) => (
                    <option key={strategy.id} value={strategy.id}>
                      {strategy.name} — {strategy.symbol} {strategy.timeframe}
                    </option>
                  ))}
                </select>
                <small className="muted-text">
                  If you also run this strategy as a cloud signal, linking them lets us compare
                  your real results against our own run of the same strategy.
                </small>
              </label>
              {error && <p className="signal-error">{error}</p>}
              <button type="button" className="primary-cta compact-button" disabled={busy} onClick={createToken}>
                {busy ? 'Checking…' : 'Get my token'}
              </button>
            </div>
          )}

          {step === 1 && (
            <div className="signal-step">
              <div className="live-callout">
                <strong>This is the step people miss.</strong>
                <p className="muted-text">
                  MetaTrader blocks EAs from reaching the internet until you allow the address.
                  Without it your EA will report error 4014 and nothing will appear here.
                </p>
              </div>
              <ol className="live-steps">
                <li>In MetaTrader 5, open <strong>Tools → Options</strong>.</li>
                <li>Go to the <strong>Expert Advisors</strong> tab.</li>
                <li>Tick <strong>Allow WebRequest for listed URL</strong>.</li>
                <li>Paste the address below into the list.</li>
                <li>Press <strong>OK</strong>.</li>
              </ol>
              <CopyField value={WEBREQUEST_ORIGIN} label="Address to add" />
              <div className="inline-actions">
                <button type="button" className="ghost-button compact-button" onClick={() => setStep(0)}>Back</button>
                <button type="button" className="primary-cta compact-button" onClick={() => setStep(2)}>
                  Done — next
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="signal-step">
              <p className="muted-text">
                Open your EA's settings in MetaTrader (right-click the chart → Expert Advisors →
                Properties), find the <strong>Live Tracking (VixLink)</strong> group and set:
              </p>
              <ol className="live-steps">
                <li><strong>Send live results</strong> → <code>true</code></li>
                <li>
                  <strong>Link token</strong> → already filled in for EAs exported from here.
                  If it is empty, paste this:
                </li>
              </ol>
              <CopyField value={link?.token || ''} label="Your link token" />
              {reusedExisting && (
                <p className="muted-text">
                  This is your existing token — it already matches any EA you have exported, so
                  there is usually nothing to paste.
                </p>
              )}
              <p className="muted-text">
                Then press OK. Reporting starts within about a minute.
              </p>
              <div className="inline-actions">
                <button type="button" className="ghost-button compact-button" onClick={() => setStep(1)}>Back</button>
                <button type="button" className="primary-cta compact-button" onClick={startWaiting}>
                  I've done that
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="signal-step">
              {connectedAccount ? (
                <div className="live-success">
                  <span className="signal-pill ok">Connected</span>
                  <h4>Account {connectedAccount.accountLogin} is reporting</h4>
                  <p className="muted-text">
                    {connectedAccount.broker || 'Your broker'} · balance{' '}
                    {Number(connectedAccount.balance || 0).toFixed(2)}
                  </p>
                  <button
                    type="button"
                    className="primary-cta compact-button"
                    onClick={() => onConnected?.(connectedAccount)}
                  >
                    View my live account
                  </button>
                </div>
              ) : (
                <div className="live-waiting">
                  <div className="live-pulse" aria-hidden="true" />
                  <h4>Waiting for your first report…</h4>
                  <p className="muted-text">
                    Leave MetaTrader open with the EA attached to a chart. The first report
                    usually arrives within a minute.
                  </p>
                  <details>
                    <summary>Nothing happening?</summary>
                    <ul className="live-troubleshoot">
                      <li>
                        Check the <strong>Experts</strong> tab at the bottom of MetaTrader. If it
                        mentions <code>WebRequest is not allowed</code>, the address from step 2
                        was not added correctly.
                      </li>
                      <li>Confirm the EA shows a smiling face on the chart (AutoTrading enabled).</li>
                      <li>Confirm <strong>Send live results</strong> is set to <code>true</code>.</li>
                      <li>The Strategy Tester never reports — the EA must run on a live chart.</li>
                    </ul>
                  </details>
                  {waiting && <p className="muted-text">Checking every few seconds…</p>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ConnectMt5Wizard;
